/**
 * The Update Log (spec Revision 7): the entries behind the yellow star square
 * on The Hub's EVENTS tab. Pure — no Phaser and no wall-clock read (callers
 * pass `now`), so a status flip is unit-testable in both states.
 */

export type UpdateStatus = 'COMING SOON' | 'RELEASED';

export interface UpdateEntry {
  id: string;
  /** The list row and the detail panel's title. */
  title: string;
  /**
   * The release instant, ms since the epoch — ONE moment for everyone (the
   * question asked before building: "8 PM US Eastern", not the player's own
   * 8 PM). The label is what the panel prints for it.
   *
   * UNSET while the date is still to be announced: the entry reads COMING
   * SOON with no countdown, forever, and never flips to RELEASED by itself.
   * (Update 1.6 was undated until it shipped on 2026-10-29.)
   */
  releaseAt?: number;
  dateLabel: string;
  /** One line under the countdown — what the update brings. Optional. */
  description?: string;
  /**
   * Atlas keys composed into the thumbnail: the backdrop plus the floaters.
   * `layout` picks the arrangement: 'arc' (the default) scatters the floaters
   * around the frame at one size; 'hero' centres the FIRST key large — a tall
   * key keeps its aspect — with the rest spread either side of it; 'beside'
   * stands the FIRST key large on the LEFT and leaves the right to the backdrop.
   * 'ring' centres the FIRST key large and spaces the rest evenly around it.
   * 'rows' stands six keys in the middle, two rows of three — the first three
   * on top, the next three under them — leaving both sides to the backdrop.
   */
  thumbnail: {
    backdrop: string;
    floaters: string[];
    layout?: 'arc' | 'hero' | 'beside' | 'ring' | 'rows';
    /**
     * A word printed across the hero (the FIRST key) as game text — Update
     * 1.6's MUSHROOMS on its big mushroom. Text, not art, because generated
     * art cannot be trusted to spell.
     */
    caption?: string;
  };
}

/** Oldest first — the detail panel opens on the FIRST entry. */
export const UPDATES: readonly UpdateEntry[] = [
  {
    id: 'hub-1.0',
    title: 'Update 1.0: The Hub',
    // Sat, Sep 5 2026, 8:00 PM EDT (UTC−4) = Sep 6 00:00 UTC.
    releaseAt: Date.UTC(2026, 8, 6, 0, 0, 0),
    dateLabel: 'Sat, Sep 5, 8:00 PM',
    thumbnail: {
      backdrop: 'hub-moon',
      floaters: ['potion-pickle', 'potion-chili', 'tower-bow-2', 'potion-cola', 'potion-burger'],
    },
  },
  {
    id: 'hub-1.1',
    title: 'Update 1.1: The Hub Extension',
    // Sat, Sep 12 2026, 6:00 PM EDT (UTC−4) = Sep 12 22:00 UTC. Same
    // convention as 1.0: ONE instant for everyone, not the player's own 6 PM.
    releaseAt: Date.UTC(2026, 8, 12, 22, 0, 0),
    dateLabel: 'Sat, Sep 12, 6:00 PM',
    description: '2 new potions, 5 new levels, Enjoy!',
    thumbnail: {
      backdrop: 'hub-moon',
      // Rocket Rage's rocket in the middle, the two new potions either side.
      floaters: ['decor-rocket', 'potion-samurai', 'potion-oliveoil'],
      layout: 'hero',
    },
  },
  {
    id: 'capybara-1.2',
    title: 'Update 1.2: The Capybara Event',
    // Thu, Sep 17 2026, 10:00 PM EDT (UTC−4) = Sep 18 02:00 UTC. Same
    // convention as 1.0 and 1.1: ONE instant for everyone, not the player's
    // own 10 PM.
    releaseAt: Date.UTC(2026, 8, 18, 2, 0, 0),
    dateLabel: 'Thu, Sep 17, 10:00 PM',
    description: '7 new towers and an epic minigame, enjoy!',
    thumbnail: {
      // The capsule stands tall in the middle; under 'hero' the first extra
      // floater sits at x −142, which puts Galactic Capy to its LEFT.
      backdrop: 'bg-capyevent',
      floaters: ['capsule-capy', 'tower-galacticcapy-3'],
      layout: 'hero',
    },
  },
  {
    id: 'leaderboards-1.3',
    title: 'Update 1.3: Leaderboards',
    // Wed, Sep 23 2026, 12:00 PM EDT (UTC−4) = Sep 23 16:00 UTC. Same
    // convention as the others: ONE instant for everyone.
    releaseAt: Date.UTC(2026, 8, 23, 16, 0, 0),
    dateLabel: 'Wed, Sep 23, 12:00 PM',
    description: 'climb to the top, find out secrets',
    thumbnail: {
      // The leaderboard itself fills the right of the frame; King Hambi, the
      // crown for beating King Ember, stands on its left.
      backdrop: 'thumb-leaderboard',
      floaters: ['tower-kinghambi-3'],
      layout: 'beside',
    },
  },
  {
    id: 'golden-1.4',
    title: 'Update 1.4: The Golden Takeout Bag',
    // Tue, Sep 29 2026, 9:00 PM EDT (UTC−4) = Sep 30 01:00 UTC. Same
    // convention as the others: ONE instant for everyone.
    releaseAt: Date.UTC(2026, 8, 30, 1, 0, 0),
    dateLabel: 'Tue, Sep 29, 9:00 PM',
    description: 'the restaurant is back, busier than ever!, order now at the restaurant.',
    thumbnail: {
      // The Golden bag in the middle of the Restaurant's kitchen, with every
      // tower it can hold around it (GOLDEN_BAG_TOWERS, in the bag's order).
      backdrop: 'bg-shop',
      floaters: [
        'takeout-golden',
        'tower-dagger-1',
        'tower-leafstorm-1',
        'tower-samurai-1',
        'tower-masher-1',
        'tower-grease-1',
        'tower-ultrahammy-4',
      ],
      layout: 'ring',
    },
  },
  {
    id: 'origin-1.5',
    title: 'Update 1.5: The Origin Event',
    // Wed, Oct 14 2026, 7:00 PM EDT (UTC−4; DST runs to Nov 1) = Oct 14
    // 23:00 UTC. Same convention as the others: ONE instant for everyone.
    releaseAt: Date.UTC(2026, 9, 14, 23, 0, 0),
    dateLabel: 'Wed, Oct 14, 7:00 PM',
    description:
      'from north to south, enjoy these new towers and levels, codes are also coming. Use code Hambi',
    thumbnail: {
      // Sunset Horizon (Above the Clouds) on the left melting into the Shadow
      // Temple (The Dark Shadows) on the right, and the event's six towers on
      // the seam at tier 3: the Light Shop's three on top, the Dark Shop's
      // three under them — north to south.
      backdrop: 'thumb-origin',
      floaters: [
        'tower-healer-3',
        'tower-banker-3',
        'tower-angelic-3',
        'tower-horror-3',
        'tower-demonic-3',
        'tower-hamreaper-3',
      ],
      layout: 'rows',
    },
  },
  {
    id: 'shroom-1.6',
    title: 'Update 1.6: The Shroom Update',
    // Released with the prod deploy (the user's call, 2026-10-29): Thu, Oct 29
    // 2026, 3:00 PM EDT (UTC−4; DST runs to Nov 1) = Oct 29 19:00 UTC.
    releaseAt: Date.UTC(2026, 9, 29, 19, 0, 0),
    dateLabel: 'Thu, Oct 29, 3:00 PM',
    description: 'Explore a world of shrooms and fungi in this new chapter.',
    thumbnail: {
      // A big mushroom in the middle of a dusky mushroom forest, MUSHROOMS
      // printed across its cap.
      backdrop: 'thumb-shrooms',
      floaters: ['decor-bigmushroom'],
      layout: 'hero',
      caption: 'MUSHROOMS',
    },
  },
];

/** COMING SOON until the instant passes (or while there is none), RELEASED from then on. */
export function updateStatus(u: UpdateEntry, now: number): UpdateStatus {
  return u.releaseAt !== undefined && now >= u.releaseAt ? 'RELEASED' : 'COMING SOON';
}

/**
 * The countdown line under the date: `2d 03:15:09` / `03:15:09` while the
 * release is ahead, and `Released!` once it has passed.
 */
export function updateCountdown(u: UpdateEntry, now: number): string {
  if (u.releaseAt === undefined) return 'Coming soon';
  const left = u.releaseAt - now;
  if (left <= 0) return 'Released!';
  const total = Math.floor(left / 1000);
  const days = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}
